import type { MenuAction, NavigateDirection } from './menu-actions';
import type { FloatingContext } from '@floating-ui/react';
import { createContext, useContext } from 'react';

export interface MenuContextValue {
    /** Whether the menu is currently open */
    open: boolean;
    /** Close the entire menu tree (this menu + all ancestors) */
    close: () => void;
    /** Ref setter for the trigger element (from useFloating) */
    setReference: (node: HTMLElement | null) => void;
    /** Ref setter for the floating content element (from useFloating) */
    setFloating: (node: HTMLElement | null) => void;
    /** Props to spread onto the trigger element. Accepts optional user props to compose event handlers. */
    getTriggerProps: (userProps?: Record<string, unknown>) => Record<string, unknown>;
    /** Props to spread onto the floating content element. Accepts optional user props to compose event handlers. */
    getFloatingProps: (userProps?: Record<string, unknown>) => Record<string, unknown>;
    /** Positioning styles for the floating content */
    floatingStyles: React.CSSProperties;
    /** Floating-ui context (for transitions and other hooks) */
    floatingContext: FloatingContext;
    /** Current highlighted item index among navigable items (-1 = none) */
    highlightedIndex: number;
    /** Dispatch a menu action */
    dispatch: (action: MenuAction) => void;
    /** Ref to the content container, used for DOM queries */
    contentRef: React.RefObject<HTMLDivElement | null>;
    /** Navigate items by direction (queries DOM and dispatches) */
    navigateItems: (direction: NavigateDirection) => void;
}

const MenuContext = createContext<MenuContextValue | null>(null);
export const MenuProvider = MenuContext.Provider;

export function useMenuContext(): MenuContextValue {
    const ctx = useContext(MenuContext);
    if (!ctx) throw new Error('Menu compound components must be used within <Menu>');
    return ctx;
}

export interface MenuRadioGroupContextValue {
    name: string;
    value: string;
    onValueChange: (value: string) => void;
}

const MenuRadioGroupContext = createContext<MenuRadioGroupContextValue | null>(null);
export const MenuRadioGroupProvider = MenuRadioGroupContext.Provider;

export function useMenuRadioGroupContext(): MenuRadioGroupContextValue {
    const ctx = useContext(MenuRadioGroupContext);
    if (!ctx) throw new Error('MenuRadioItem must be used within <MenuRadioGroup>');
    return ctx;
}
